/**
 * Pointer lifecycle of calendar drags (mouse, pen, touch): a press becomes a drag after DRAG_THRESHOLD_PX (mouse) or
 * after a long press (touch, so swiping still scrolls); Esc and pointercancel abort; the click that follows a drag is
 * suppressed. Callers get viewport coordinates and do the geometry themselves (drag.ts).
 */
import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { DRAG_THRESHOLD_PX, LONG_PRESS_MS } from './drag';

export interface DragCallbacks {
  /** Pointer moved while dragging (also once when a touch long-press starts). */
  move: (x: number, y: number) => void;
  /** Released: `click` = it never became a drag (a tap or click). */
  end: (click: boolean) => void;
  /** Esc, pointercancel or a touch that turned into scrolling. */
  cancel: () => void;
}

export function usePointerDrag() {
  const cleanupRef = useRef<(() => void) | undefined>(undefined);
  const suppressClick = useRef(false);

  useEffect(() => () => cleanupRef.current?.(), []);

  const begin = useCallback((event: ReactPointerEvent<HTMLElement>, callbacks: DragCallbacks) => {
    if (event.button !== 0) return;
    cleanupRef.current?.();
    const id = event.pointerId;
    const touch = event.pointerType === 'touch';
    const sx = event.clientX;
    const sy = event.clientY;
    let active = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const block = (e: TouchEvent) => e.preventDefault();

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('keydown', onKey, true);
      document.removeEventListener('touchmove', block);
      cleanupRef.current = undefined;
    };
    const activate = () => {
      active = true;
      // A text selection made by the drag would make the right-click on the result open the browser's menu
      // (copy) instead of ours (DESIGN.md §18.3; live finding 2026-10-12).
      window.getSelection()?.removeAllRanges();
      // Once a touch drag runs, the page must not scroll under the finger.
      if (touch) document.addEventListener('touchmove', block, { passive: false });
    };
    const finishDrag = () => {
      suppressClick.current = true;
      setTimeout(() => (suppressClick.current = false), 0);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      const distance = Math.hypot(e.clientX - sx, e.clientY - sy);
      if (!active) {
        if (touch) {
          if (distance > 8) {
            cleanup(); // the finger scrolls; no drag, no tap
            callbacks.cancel();
          }
          return;
        }
        if (distance < DRAG_THRESHOLD_PX) return;
        activate();
      }
      e.preventDefault();
      callbacks.move(e.clientX, e.clientY);
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      const wasActive = active;
      if (wasActive) window.getSelection()?.removeAllRanges();
      cleanup();
      if (wasActive) finishDrag();
      callbacks.end(!wasActive);
    };
    const onCancel = (e: PointerEvent) => {
      if (e.pointerId !== id) return;
      cleanup();
      callbacks.cancel();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      const wasActive = active;
      cleanup();
      if (wasActive) finishDrag();
      callbacks.cancel();
    };

    if (touch) {
      timer = setTimeout(() => {
        activate();
        callbacks.move(sx, sy);
      }, LONG_PRESS_MS);
    }
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('keydown', onKey, true);
    cleanupRef.current = cleanup;
  }, []);

  return { begin, suppressClick };
}
